<!-- Patient_Detail_Info.vue -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 p-5">
    <!-- Loading -->
    <div v-if="loading" class="space-y-4">
      <div>
        <div class="h-4 w-36 bg-gray-200 rounded animate-pulse"></div>
        <div class="flex items-center gap-3 mt-1.5">
          <div class="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
          <div class="h-3 w-12 bg-gray-100 rounded animate-pulse"></div>
          <div class="h-3 w-14 bg-gray-100 rounded animate-pulse"></div>
          <div class="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
        </div>
        <div class="flex gap-1.5 mt-2">
          <div class="h-5 w-14 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
      <div class="pt-3 border-t border-gray-100">
        <div class="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
          <div v-for="n in 4" :key="'sk-demo-' + n" class="contents">
            <div class="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [100, 80, 120, 60][n - 1] + 'px' }"></div>
          </div>
        </div>
      </div>
      <div class="pt-3 border-t border-gray-100">
        <div class="h-3 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
          <div v-for="n in 3" :key="'sk-care-' + n" class="contents">
            <div class="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [140, 90, 110][n - 1] + 'px' }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- No data -->
    <div v-else-if="!resolvedPatientData" class="text-center py-8 text-xs text-gray-400">
      No patient data available
    </div>

    <template v-else>
      <!-- Patient Name Header -->
      <div class="mb-4">
        <div class="sensitive text-sm font-semibold text-gray-900">{{ patientDisplayName }}</div>
        <div class="sensitive flex items-center gap-3 mt-0.5 text-xs text-gray-500">
          <span>{{ patientIdentifier }}</span>
          <span v-if="patientAge">{{ patientAge }} yrs</span>
          <span v-if="patientGender">{{ patientGender }}</span>
          <span v-if="patientDob">DOB {{ patientDob }}</span>
        </div>
        <PreferredNameField
          v-if="preferredNameAttribute && entityId"
          class="mt-1.5"
          :entity-id="entityId"
          :attribute="preferredNameAttribute"
          :loading="entityAttributesLoading"
          @updated="handlePreferredNameUpdate"
        />
        <div v-if="hasStatusBadges || patientFlags.length" class="flex items-center flex-wrap gap-1.5 mt-2">
          <!-- Configurable patient flags (studio metrics, metric_kind='patient_flag'),
               sorted by priority. Source: /api/returnpatientdata/ data_type='patient_flags'. -->
          <span
            v-for="flag in patientFlags"
            :key="flag.metric_id"
            class="relative inline-flex group/flag"
          >
            <span
              class="px-1.5 py-px text-xs font-medium rounded"
              :class="flagBadgeClass(flag.color)"
            >{{ flagDisplayLabel(flag) }}</span>
            <!-- Rich hover popover: underlying flag VALUE formatted per the
                 flag's flag_config.value_display (backend resolve_flag_display). -->
            <span
              v-if="flag.value_label"
              class="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden -translate-x-1/2 group-hover/flag:block"
              role="tooltip"
            >
              <span class="block rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg whitespace-nowrap">
                <span class="block font-semibold">{{ flag.label }}</span>
                <span class="block text-gray-300">{{ flag.value_label }}</span>
                <span class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
              </span>
            </span>
          </span>
          <!-- Legacy direct-column badges (kept for tenants without flag metrics) -->
          <span v-if="resolvedPatientData && resolvedPatientData.bene_death_dt && !hasFlag('Deceased')" class="px-1.5 py-px text-xs font-medium rounded bg-red-100 text-red-700">Deceased {{ formatDate(resolvedPatientData.bene_death_dt) }}</span>
          <span v-if="resolvedPatientData && resolvedPatientData.esrd" class="px-1.5 py-px text-xs font-medium rounded bg-orange-100 text-orange-700">ESRD</span>
          <span v-if="resolvedPatientData && resolvedPatientData.disabled" class="px-1.5 py-px text-xs font-medium rounded bg-yellow-100 text-yellow-700">Disabled</span>
          <span v-if="resolvedPatientData && resolvedPatientData.full_dual && !hasFlag('Dual')" class="px-1.5 py-px text-xs font-medium rounded bg-purple-100 text-purple-700">Full Dual</span>
          <span v-if="resolvedPatientData && resolvedPatientData.partial_dual && !hasFlag('Dual')" class="px-1.5 py-px text-xs font-medium rounded bg-primary-100 text-primary-700">Partial Dual</span>
        </div>
      </div>

      <!-- Demographics -->
      <div v-if="demographicFields.length" class="mb-4 pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Demographics</div>
        <div class="sensitive grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <template v-for="field in demographicFields" :key="field.label">
            <span class="text-xs text-gray-500">{{ field.label }}</span>
            <span class="text-xs text-gray-900">{{ field.value }}</span>
          </template>
        </div>
      </div>

      <!-- IHCI only: populated from EMPI.PATIENT_IDENTIFIER by the patient-info response. -->
      <div v-if="hasEpicMrnField" class="mb-4 pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Epic MRNs</div>
        <div class="sensitive grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <template v-if="epicMrns.length">
            <template v-for="mrn in epicMrns" :key="mrn.value">
              <span class="text-gray-500">MRN</span>
              <span class="flex flex-wrap items-center gap-1.5 text-gray-900">
                {{ mrn.value }}
                <span
                  v-if="mrn.is_preferred"
                  class="rounded bg-primary-100 px-1.5 py-px text-[10px] font-medium text-primary-700"
                >Preferred</span>
              </span>
            </template>
          </template>
          <template v-else>
            <span class="text-gray-500">MRN</span>
            <span class="text-gray-400">Not available</span>
          </template>
        </div>
      </div>

      <!-- Care Context -->
      <div v-if="careContextFields.length || attribution" class="mb-4 pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Care Context</div>
        <div v-if="careContextFields.length" class="sensitive grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <template v-for="field in careContextFields" :key="field.label">
            <span class="text-xs text-gray-500">{{ field.label }}</span>
            <span class="text-xs text-gray-900">{{ field.value }}</span>
          </template>
        </div>

        <!-- Attribution (ASSIGN_CURRENT, most-recent): PCP + practice + contract -->
        <div v-if="attribution" class="mt-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 space-y-1">
          <div v-if="attributionPcp" class="flex items-center gap-2 text-xs">
            <FontAwesomeIcon :icon="['fas', 'user-doctor']" class="text-sky-500 text-[12px] shrink-0" />
            <span class="text-gray-500">PCP</span>
            <span class="font-medium text-gray-900 truncate sensitive">{{ attributionPcp }}</span>
            <span v-if="attribution.primary_npi" class="text-gray-400 tabular-nums">· {{ attribution.primary_npi }}</span>
          </div>
          <div v-if="attributionPractice" class="flex items-center gap-2 text-xs">
            <FontAwesomeIcon :icon="['fas', 'hospital']" class="text-indigo-500 text-[12px] shrink-0" />
            <span class="text-gray-500">Practice</span>
            <span class="font-medium text-gray-900 truncate sensitive">{{ attributionPractice }}</span>
          </div>
          <div v-if="attribution.contract_name" class="flex items-center gap-2 text-xs">
            <FontAwesomeIcon :icon="['fas', 'file-contract']" class="text-violet-500 text-[12px] shrink-0" />
            <span class="text-gray-500">Contract</span>
            <span class="font-medium text-gray-900 truncate">{{ attribution.contract_name }}</span>
            <span v-if="attribution.performance_year" class="text-gray-400">· PY{{ attribution.performance_year }}</span>
          </div>
        </div>
      </div>

      <!-- Next Appointment -->
      <div v-if="nextAppointment" class="mb-4 pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Next Appointment</div>
        <div class="flex items-start gap-3 bg-green-50 rounded-lg px-3 py-2 border border-green-200">
          <FontAwesomeIcon :icon="['fas', 'calendar-check']" class="text-green-600 text-sm mt-0.5 shrink-0" />
          <div class="flex-1 min-w-0">
            <div class="text-xs font-semibold text-gray-900">
              {{ formatDate(nextAppointment.APPOINTMENT_DATE) }}
            </div>
            <div v-if="nextAppointment.APPT_TYPE" class="text-xs text-gray-600 mt-0.5">
              {{ nextAppointment.APPT_TYPE }}
            </div>
            <div v-if="nextAppointment.CLINICIAN" class="sensitive text-xs text-gray-500 mt-0.5" title="Appointment information is pulled from the practice EHR. May differ from attributed PCP.">
              <span class="text-gray-400">with</span> {{ nextAppointment.CLINICIAN }}
            </div>
            <div v-if="nextAppointment.STATUS" class="text-xs text-gray-400 mt-0.5">
              {{ nextAppointment.STATUS }}
            </div>
          </div>
          <div :class="['text-xs font-medium shrink-0 mt-0.5', isFutureAppointment ? 'text-green-700' : 'text-gray-400']">
            {{ daysUntilAppointment }}
          </div>
        </div>
      </div>

      <!-- Latest Appointment -->
      <div v-if="latestAppointment && (!nextAppointment || latestAppointment._key !== nextAppointment._key)" class="mb-4 pt-3 border-t border-gray-100">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Latest Appointment</div>
        <div class="flex items-start gap-3 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
          <FontAwesomeIcon :icon="['fas', 'calendar']" class="text-gray-400 text-sm mt-0.5 shrink-0" />
          <div class="flex-1 min-w-0">
            <div class="text-xs font-semibold text-gray-900">
              {{ formatDate(latestAppointment.APPOINTMENT_DATE) }}
            </div>
            <div v-if="latestAppointment.APPT_TYPE" class="text-xs text-gray-600 mt-0.5">
              {{ latestAppointment.APPT_TYPE }}
            </div>
            <div v-if="latestAppointment.CLINICIAN" class="sensitive text-xs text-gray-500 mt-0.5" title="Appointment information is pulled from the practice EHR. May differ from attributed PCP.">
              <span class="text-gray-400">with</span> {{ latestAppointment.CLINICIAN }}
            </div>
          </div>
          <div class="text-xs text-gray-400 font-medium shrink-0 mt-0.5">
            {{ latestAppointment.STATUS }}
          </div>
        </div>
      </div>

      <!-- Contact -->
      <div class="pt-3 border-t border-gray-100">
        <Patient_ContactEntries
          :patient-identifier="patientIdentifier"
          :payer-name="payerName"
          @show-message="(text, type) => $emit('show-message', text, type)"
        />
      </div>

      <!-- Patient Notes -->
      <div v-if="entityId" class="pt-3 border-t border-gray-100">
        <div class="flex items-center justify-between mb-1.5">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notes</div>
          <button
            v-if="!showNoteInput"
            @click="editingNoteId = null; editNoteText = ''; showNoteInput = true"
            class="text-xs text-primary-600 hover:text-primary-700"
          >+ Add</button>
        </div>
        <!-- Add note form -->
        <div v-if="showNoteInput" class="mb-2">
          <textarea
            v-model="newNoteText"
            rows="3"
            class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-y"
            placeholder="Add a note (e.g. caregiver contact info, patient preferences)..."
          ></textarea>
          <div class="flex items-center justify-end gap-2 mt-1">
            <button @click="showNoteInput = false; newNoteText = ''" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button
              @click="saveNote"
              :disabled="savingNote || !newNoteText.trim()"
              class="px-3 py-1 text-xs text-white rounded transition-colors"
              :style="{ backgroundColor: newNoteText.trim() && !savingNote ? '#F76734' : '#D1D5DB' }"
            >{{ savingNote ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
        <!-- Existing notes -->
        <div v-if="loadingNotes" class="text-xs text-gray-400 py-2">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" /> Loading…
        </div>
        <div v-else-if="patientNotes.length === 0 && !showNoteInput" class="text-xs text-gray-400 py-1">
          No notes yet
        </div>
        <div v-else class="space-y-2">
          <div v-for="note in patientNotes" :key="note.id" class="bg-gray-50 rounded-lg px-3 py-2">
            <template v-if="editingNoteId === note.id">
              <textarea
                v-model="editNoteText"
                rows="3"
                class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-y"
                placeholder="Add a note (e.g. caregiver contact info, patient preferences)..."
              ></textarea>
              <div class="flex items-center justify-end gap-2 mt-1">
                <button class="text-xs text-gray-500 hover:text-gray-700" @click="cancelEditNote">Cancel</button>
                <button
                  :disabled="savingNote || !editNoteText.trim()"
                  class="px-3 py-1 text-xs text-white rounded transition-colors"
                  :style="{ backgroundColor: editNoteText.trim() && !savingNote ? '#F76734' : '#D1D5DB' }"
                  @click="saveEditNote"
                >{{ savingNote ? 'Saving…' : 'Save' }}</button>
              </div>
            </template>
            <div v-else class="sensitive text-xs text-gray-900 whitespace-pre-wrap">{{ note.text }}</div>
            <div class="flex items-center gap-2 mt-1">
              <span class="text-[10px] text-gray-400">{{ formatNoteDate(note.created_at) }}</span>
              <span class="sensitive text-[10px] text-gray-400">{{ note.appuser_name }}</span>
              <button
                v-if="isNoteAuthor(note) && editingNoteId !== note.id"
                class="text-gray-300 hover:text-gray-500 text-[10px]"
                aria-label="Edit note"
                @click="startEditNote(note)"
              >
                <FontAwesomeIcon :icon="['fas', 'pencil']" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Enrolled Programs summary -->
      <div class="pt-3 border-t border-gray-100">
        <Opportunities_Summary
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
        />
      </div>

      <!-- Utilization Fast Facts (last 12 mo, graph-sourced) -->
      <div class="pt-3 border-t border-gray-100">
        <Utilization_FastFacts
          :claims="graphClaims"
          :adt-episodes="graphAdtEpisodes"
          :loading="graphLoading"
          :not-in-graph="graphNotFound"
          :patient-identifier="patientIdentifier"
          @refresh="fetchClinicalGraph"
        />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Patient_ContactEntries from './Patient_ContactEntries.vue';
import Utilization_FastFacts from '@/components/Patient_Drawer/Clinical_History/Utilization_FastFacts.vue';
import Opportunities_Summary from '@/components/Patient_Drawer/Clinical_History/Opportunities_Summary.vue';
import PreferredNameField from '@/components/Patient_Drawer/PreferredNameField.vue';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_Detail_Info',
  components: {
    FontAwesomeIcon,
    Patient_ContactEntries,
    Utilization_FastFacts,
    Opportunities_Summary,
    PreferredNameField,
  },
  mixins: [abortable],
  props: {
    patientIdentifier: {
      type: String,
      required: true
    },
    entityId: {
      type: Number,
      required: false
    },
    patientData: {
      type: Object,
      default: null
    },
    payerName: {
      type: String,
      default: null,
    },
    entityAttributes: {
      type: Object,
      default: null,
    },
    entityAttributesLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['show-message', 'preferred-name-updated'],
  data() {
    return {
      loading: false,
      fetchedPatientData: null,
      patientFlags: [],
      nextAppointment: null,
      latestAppointment: null,
      // Utilization Fast Facts — graph clinical payload
      graphClaims: [],
      graphAdtEpisodes: [],
      graphLoading: false,
      graphNotFound: false,
      // Attribution (most-recent ASSIGN_CURRENT row: PCP, contract, origin)
      attribution: null,
      attributionLoading: false,
      // Patient notes
      patientNotes: [],
      loadingNotes: false,
      showNoteInput: false,
      newNoteText: '',
      savingNote: false,
      editingNoteId: null,
      editNoteText: '',
    };
  },
  computed: {
    resolvedPatientData() {
      // Merge, don't pick: the queue-row prop (patientData) is thin -- it
      // carries name + DOB but not age/gender/flags. Those come only from the
      // demographics fetch. Preferring the prop wholesale (patientData ||
      // fetchedPatientData) discarded the fetched age/Female for patients
      // opened with a prop (e.g. Linda Farrar). Prop values win where present;
      // the fetch fills every gap.
      if (!this.patientData) return this.fetchedPatientData;
      if (!this.fetchedPatientData) return this.patientData;
      return { ...this.fetchedPatientData, ...this.stripNullish(this.patientData) };
    },
    patientDisplayName() {
      const d = this.resolvedPatientData;
      if (d?.bene_last_name && d?.bene_first_name) return `${d.bene_last_name}, ${d.bene_first_name}`;
      if (d?.patient_name) return d.patient_name;
      return this.patientIdentifier;
    },
    patientAge() {
      // Derive age from DOB (birthday-aware) rather than trusting the
      // pre-computed bene_age, which is DATEDIFF('year',...) upstream and
      // rounds up before the birthday (e.g. shows 73 for a Nov-1953 DOB in
      // July). DOB arrives as a YYYY-MM-DD string; parse the parts directly
      // to avoid timezone day-shift.
      const dob = this.resolvedPatientData?.bene_dob || this.resolvedPatientData?.patient_dob;
      if (!dob) return null;
      const parts = String(dob).substring(0, 10).split('-');
      if (parts.length !== 3) return null;
      const by = Number(parts[0]);
      const bm = Number(parts[1]);
      const bd = Number(parts[2]);
      if (!by || !bm || !bd) return null;
      const now = new Date();
      let age = now.getFullYear() - by;
      // Subtract a year if the birthday hasn't occurred yet this year.
      const m = now.getMonth() + 1;
      const d = now.getDate();
      if (m < bm || (m === bm && d < bd)) age -= 1;
      return age >= 0 && age < 130 ? age : null;
    },
    patientGender() {
      const g = this.formatGender(this.resolvedPatientData?.bene_sex_cd);
      return g !== 'N/A' ? g : null;
    },
    patientDob() {
      const d = this.formatDate(this.resolvedPatientData?.bene_dob || this.resolvedPatientData?.patient_dob);
      return d !== 'N/A' ? d : null;
    },
    hasStatusBadges() {
      const d = this.resolvedPatientData;
      return d && (d.esrd || d.disabled || d.full_dual || d.partial_dual || d.bene_death_dt);
    },
    demographicFields() {
      const d = this.resolvedPatientData;
      if (!d) return [];
      return [
        { label: 'HCC Model', value: d.hcc_model || null },
        { label: d.other_patient_identifier_type || 'Alt ID', value: d.other_patient_identifier || null },
        { label: 'FHIR ID', value: d.fhir_patient_id || null },
        { label: 'Language', value: d.language || null },
      ].filter(f => f.value);
    },
    hasEpicMrnField() {
      return Array.isArray(this.resolvedPatientData?.epic_mrns);
    },
    epicMrns() {
      if (!this.hasEpicMrnField) return [];
      return this.resolvedPatientData.epic_mrns.filter(mrn => mrn?.value);
    },
    careContextFields() {
      const d = this.resolvedPatientData;
      if (!d) return [];
      // PCP / Practice / Contract are consolidated into the attribution block
      // below; keep payer/LOB/last-service here.
      return [
        { label: 'Payer', value: d.payer_name || null },
        { label: 'LOB', value: d.lob || null },
        { label: 'Last Service', value: d.last_service_date ? this.formatDate(d.last_service_date) : null },
      ].filter(f => f.value);
    },
    attributionPcp() {
      const a = this.attribution;
      if (!a || !a.pcp_name) return null;
      return String(a.pcp_name).replace(/,?\s*$/, '').trim() || null;
    },
    attributionPractice() {
      const a = this.attribution || {};
      return a.entity_name || (this.resolvedPatientData && this.resolvedPatientData.practice_name) || null;
    },
    isFutureAppointment() {
      if (!this.nextAppointment?.APPOINTMENT_DATE) return false;
      return this.nextAppointment.APPOINTMENT_DATE >= new Date().toISOString().split('T')[0];
    },
    daysUntilAppointment() {
      if (!this.nextAppointment?.APPOINTMENT_DATE) return '';
      const [y, m, d] = this.nextAppointment.APPOINTMENT_DATE.split('-').map(Number);
      const apptDate = new Date(y, m - 1, d);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.ceil((apptDate - today) / 86400000);
      if (diff === 0) return 'Today';
      if (diff === 1) return 'Tomorrow';
      if (diff < 0) return `${Math.abs(diff)}d ago`;
      return `In ${diff}d`;
    },
    preferredNameAttribute() {
      if (!this.entityAttributes) return null;
      if (!this.entityAttributes['_defined_attributes']?.includes('preferred_name')) return null;
      const group = this.entityAttributes['patient_info'] || [];
      const attr = group.find((a) => a.name === 'preferred_name');
      return {
        name: 'preferred_name',
        display_name: 'Preferred Name',
        field_type: 'text',
        value: attr?.value || null,
        data_source: attr?.data_source || null,
        confidence: null,
        updated: attr?.updated || null,
        reason: null,
        is_required: false,
        description: '',
        display_order: 0,
      };
    },
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const signal = this._renewAbortSignal('patient');
          this.fetchPatientData(signal);
          this.fetchNextAppointment(signal);
          this.fetchPatientFlags(signal);
          this.fetchClinicalGraph(signal);
          this.fetchAttribution(signal);
        }
      }
    },
    entityId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const signal = this._renewAbortSignal('notes');
          this.fetchNotes(signal);
        }
      }
    },
  },
  methods: {
    handlePreferredNameUpdate(value) {
      this.$emit('preferred-name-updated', value);
    },
    // Drop null/undefined/'' keys so a prop's empty fields don't overwrite a
    // populated fetched value during the merge in resolvedPatientData.
    stripNullish(obj) {
      if (!obj) return {};
      return Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '')
      );
    },
    async fetchClinicalGraph(signal) {
      if (!this.patientIdentifier) return;
      this.graphLoading = true;
      this.graphNotFound = false;
      try {
        const { data } = await axios.get(
          `/api/graph/patient/${this.patientIdentifier}/clinical/`,
          { signal },
        );
        this.graphClaims = data.claims || [];
        this.graphAdtEpisodes = data.adt_episodes || [];
      } catch (e) {
        if (this._isAborted(e)) return;
        this.graphClaims = [];
        this.graphAdtEpisodes = [];
        if (e?.response?.status === 404) {
          this.graphNotFound = true;
        } else {
          console.error('Failed to fetch clinical graph for fast facts:', e);
        }
      } finally {
        if (!signal?.aborted) this.graphLoading = false;
      }
    },
    async fetchAttribution(signal) {
      if (!this.patientIdentifier) return;
      this.attributionLoading = true;
      try {
        const { data } = await axios.post('/api/returnpatientdata/', {
          patient_identifier: this.patientIdentifier,
          data_type: 'attrib',
          attribution_type: 'most_recent',
        }, { signal });
        this.attribution = Array.isArray(data) && data.length ? data[0] : null;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.attribution = null;
      } finally {
        if (!signal?.aborted) this.attributionLoading = false;
      }
    },
    async fetchNotes(signal) {
      if (!this.entityId) return;
      this.editingNoteId = null;
      this.editNoteText = '';
      this.loadingNotes = true;
      try {
        const { data } = await axios.get('/api/work/', {
          params: { entity: this.entityId, work_type: 292 },
          signal,
        });
        const results = data.results || data || [];
        this.patientNotes = results
          .filter(w => w.metadata?.form_data?.note)
          .map(w => ({
            id: w.id,
            text: w.metadata.form_data.note,
            created_at: w.created_at,
            appuser: w.appuser,
            appuser_name: this.formatAppuserName(w.appuser_name),
          }))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } catch (e) {
        if (this._isAborted(e)) return;
        console.error('Error fetching patient notes:', e);
      } finally {
        if (!signal?.aborted) this.loadingNotes = false;
      }
    },
    async saveNote() {
      if (!this.newNoteText.trim()) return;
      this.savingNote = true;
      try {
        await axios.post('/api/work/', {
          entity: this.entityId,
          work_type: 292,
          appuser: this.$store.getters['auth/current_user']?.id,
          metadata: { form_data: { note: this.newNoteText.trim() } },
        });
        this.newNoteText = '';
        this.showNoteInput = false;
        await this.fetchNotes();
      } catch (e) {
        console.error('Error saving note:', e);
      } finally {
        this.savingNote = false;
      }
    },
    isNoteAuthor(note) {
      return note.appuser === this.$store.getters['auth/current_user']?.id;
    },
    startEditNote(note) {
      this.editingNoteId = note.id;
      this.editNoteText = note.text;
      this.showNoteInput = false;
    },
    cancelEditNote() {
      this.editingNoteId = null;
      this.editNoteText = '';
    },
    async saveEditNote() {
      const text = this.editNoteText.trim();
      if (!text || !this.editingNoteId) return;

      const noteId = this.editingNoteId;
      this.savingNote = true;
      try {
        await axios.patch(`/api/work/${noteId}/`, {
          metadata: { form_data: { note: text } },
        });
        const note = this.patientNotes.find(item => item.id === noteId);
        if (note) note.text = text;
        this.cancelEditNote();
      } catch (e) {
        console.error('Error editing note:', e);
      } finally {
        this.savingNote = false;
      }
    },
    formatNoteDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    },
    formatAppuserName(name) {
      if (!name) return '';
      if (name.includes(',')) return name.split(',').map(s => s.trim()).reverse().join(' ');
      return name;
    },
    async fetchPatientData(signal) {
      if (!this.patientIdentifier) return;
      this.loading = true;
      try {
        const response = await this.$axios.post('/api/returnpatientdata/', {
          patient_identifier: this.patientIdentifier,
          data_type: 'patient_info'
        }, { signal });
        if (response.data && response.data.length > 0) {
          this.fetchedPatientData = response.data.reduce((acc, curr) => {
            Object.keys(curr).forEach(key => {
              if (curr[key] !== null && curr[key] !== undefined && acc[key] === undefined) {
                acc[key] = curr[key];
              }
            });
            return acc;
          }, {});
        }
      } catch (err) {
        if (this._isAborted(err)) return;
        console.error('Error loading patient data:', err);
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },

    async fetchPatientFlags(signal) {
      if (!this.patientIdentifier) return;
      this.patientFlags = [];
      try {
        const { data } = await this.$axios.post('/api/returnpatientdata/', {
          data_type: 'patient_flags',
          patient_identifier: this.patientIdentifier,
        }, { signal });
        this.patientFlags = Array.isArray(data) ? data : [];
      } catch (e) {
        if (this._isAborted(e)) return;
        this.patientFlags = [];
      }
    },
    hasFlag(label) {
      return this.patientFlags.some(f => (f.label || '').toLowerCase() === label.toLowerCase());
    },
    flagDisplayLabel(flag) {
      // "Deceased with DOD" per the IHCI mockup: append the death date when known.
      if ((flag.label || '').toLowerCase() === 'deceased' && this.resolvedPatientData?.bene_death_dt) {
        return `Deceased ${this.formatDate(this.resolvedPatientData.bene_death_dt)}`;
      }
      return flag.label;
    },
    flagBadgeClass(color) {
      // Map a flag color token (set in metric display_config.flag_color) to
      // Tailwind badge classes. Falls back to gray.
      const map = {
        red: 'bg-red-100 text-red-700',
        orange: 'bg-orange-100 text-orange-700',
        yellow: 'bg-yellow-100 text-yellow-700',
        green: 'bg-green-100 text-green-700',
        blue: 'bg-blue-100 text-blue-700',
        purple: 'bg-purple-100 text-purple-700',
        primary: 'bg-primary-100 text-primary-700',
        gray: 'bg-gray-100 text-gray-700',
      };
      return map[color] || map.gray;
    },
    async fetchNextAppointment(signal) {
      if (!this.patientIdentifier) return;
      this.nextAppointment = null;
      try {
        const { data } = await this.$axios.get(
          `/api/graph/patient/${this.patientIdentifier}/subgraph/`,
          { signal },
        );
        const appointments = data.nodes?.Appointment || [];
        if (!appointments.length) return;
        const today = new Date().toISOString().split('T')[0];
        const sorted = appointments
          .filter(a => a.APPOINTMENT_DATE)
          .sort((a, b) => b.APPOINTMENT_DATE.localeCompare(a.APPOINTMENT_DATE));
        this.latestAppointment = sorted[0] || null;
        const future = sorted
          .filter(a => a.APPOINTMENT_DATE >= today)
          .sort((a, b) => a.APPOINTMENT_DATE.localeCompare(b.APPOINTMENT_DATE));
        this.nextAppointment = future.length > 0 ? future[0] : null;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.nextAppointment = null;
      }
    },

    formatGender(sexCode) {
      if (!sexCode) return 'N/A';
      if (sexCode === 1 || sexCode === '1' || sexCode === 'M') return 'Male';
      if (sexCode === 2 || sexCode === '2' || sexCode === 'F') return 'Female';
      return 'Unknown';
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const dateOnly = String(dateString).substring(0, 10);
      const parts = dateOnly.split('-');
      if (parts.length !== 3) return dateString;
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$emit('show-message', 'Copied to clipboard', 'success');
      }).catch(() => {
        this.$emit('show-message', 'Failed to copy', 'error');
      });
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
